/**
 * Mobile width caps for media elements (Image, Video, AnimatedImage, BeforeAfter).
 *
 * Round shapes (circle/square) are always capped on mobile so small desktop
 * media doesn't blow up to full width; non-round shapes only honor an explicit
 * user resize below 90% (otherwise combo/layout rules decide the width).
 *
 * Returns the `--entity-mobile-*` CSS custom properties to spread into a
 * media element's inline style alongside its own primary width var.
 */

const ROUND_SHAPES = new Set(["circle", "square"]);

/** Mobile cap % for round media inside a "narrow" combo. */
const ROUND_CAP_NARROW = 40;
/** Mobile cap % for round media in a normal-width combo. */
const ROUND_CAP_DEFAULT = 50;
/** Mobile cap % for non-round media. */
const NON_ROUND_CAP = 60;
/** User resizes at/above this are treated as "full width" — combo rules win. */
const USER_RESIZE_FULL_WIDTH_THRESHOLD = 90;

export const getMobileMediaCapVars = (
  width: number | string | null | undefined,
  shape: string | undefined,
  isNarrowCombo: boolean,
): Record<string, string> => {
  const parsed = width != null && width !== "" ? Number(width) : 100;
  const widthNum = Number.isFinite(parsed) ? parsed : 100;
  const isRound = ROUND_SHAPES.has(shape ?? "");
  const mobileCapPct = isRound
    ? isNarrowCombo
      ? ROUND_CAP_NARROW
      : ROUND_CAP_DEFAULT
    : NON_ROUND_CAP;
  const overridePct = isRound
    ? Math.min(widthNum, mobileCapPct)
    : widthNum < USER_RESIZE_FULL_WIDTH_THRESHOLD
      ? widthNum
      : null;
  return {
    "--entity-mobile-cap-by-shape": `${mobileCapPct}%`,
    ...(overridePct !== null && {
      "--entity-mobile-width-override": `${overridePct}%`,
    }),
  };
};
